import React, { useState } from "react";
import {
  YouTube as YouTubeIcon,
  LinkedIn as LinkedInIcon,
  GitHub as GitHubIcon,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import logo from "../../assets/logos/silver_gradient.webp";
import { PrivacyPolicyModal } from "../modals/PrivacyPolicyModal";
import { TermsModal } from "../modals/TermsModal";
import { AccessibilityModal } from "../modals/AccessibilityModal";
import { useCookieConsent } from "../../hooks/useCookieConsent";
import { useAuth } from "../../contexts/AuthContext";
import { trackEvent } from "../../utils/analytics";
import { FONT_MONO, MASTHEAD_BG } from "../../theme";

// X (formerly Twitter) Icon Component
const XIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" width="18" height="18" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

/** Footer links: 14px, quiet, no underline dance. */
const footerLinkSx = {
  fontSize: "0.875rem",
  color: "text.secondary",
  "&:hover": { color: "text.primary" },
} as const;

/** The ONE sanctioned recurring caps label on the site (existing strings). */
const columnLabelSx = {
  display: "block",
  mb: 2,
  color: "text.secondary",
} as const;

const socialIconSx = {
  color: "text.secondary",
  "&:hover": { color: "text.primary" },
} as const;

export const Footer: React.FC = () => {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const [email, setEmail] = useState("");
  const { setSettingsOpen } = useCookieConsent();
  const { isAdmin } = useAuth();

  const handleNewsletterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    trackEvent("newsletter_signup_attempt", { surface: "footer" });
    // Newsletter endpoint wiring is handled elsewhere; fire success optimistically for analytics.
    trackEvent("newsletter_signup_success", { surface: "footer" });
    setEmail("");
  };

  const exploreLinks = [
    { label: "Signal Desk", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Resource guides", href: "/resources" },
    { label: "Agent kits", href: "/agent-kits" },
    ...(isAdmin ? [{ label: "Training Catalog", href: "/training" }] : []),
    ...(isAdmin ? [{ label: "Certification Catalog", href: "/certifications" }] : []),
  ];

  const companyLinks = [
    { label: "About Obsidian Ridge AI", href: "/about" },
    ...(isAdmin ? [{ label: "Enterprise Enablement", href: "/enterprise" }] : []),
    { label: "Contact", href: "/contact" },
  ];

  const topicLinks = [
    { label: "GitHub Copilot", tag: "GitHub Copilot" },
    { label: "OpenAI", tag: "OpenAI" },
    { label: "Anthropic", tag: "Anthropic" },
    { label: "Agents", tag: "Agents" },
    { label: "Security", tag: "Security" },
  ];

  return (
    <Box
      component="footer"
      sx={{
        // Recessed ground: the footer sits in the `well`, one tone BELOW the
        // canvas, so the page visibly ends (Ledger 2.0 depth ladder).
        bgcolor: "surfaces.well",
        borderTop: 1,
        borderColor: "divider",
        pt: 8,
        pb: "max(64px, calc(64px + env(safe-area-inset-bottom)))",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 4 }}>
          {/* Brand Column */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
              {/* Obsidian tile keeps the silver mark on dark ground in both modes */}
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  bgcolor: MASTHEAD_BG,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <img
                  src={logo}
                  alt="Obsidian Ridge Logo"
                  width={20}
                  height={20}
                  style={{ height: 20, width: 20 }}
                />
              </Box>
              <Typography
                component="span"
                sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary" }}
              >
                Obsidian Ridge
              </Typography>
              {/* Mono "AI" chip — same wordmark lockup as the masthead, but
                  mode-aware ember since the footer follows the page ground. */}
              <Box
                component="span"
                sx={(theme) => ({
                  display: "inline-flex",
                  alignItems: "center",
                  px: 0.75,
                  py: 0.25,
                  fontFamily: FONT_MONO,
                  fontSize: "0.6875rem", // 11px
                  fontWeight: 500,
                  lineHeight: 1,
                  color: "secondary.main",
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
                  borderRadius: "4px",
                })}
              >
                AI
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
              Verified AI engineering signal for teams that ship.
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 3, ml: -1 }}>
              <IconButton
                component="a"
                href="https://x.com/ObsidianRidgeAI"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X (Twitter)"
                sx={socialIconSx}
              >
                <XIcon />
              </IconButton>
              <IconButton
                component="a"
                href="https://www.linkedin.com/in/ObsidianRidgeAI"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                sx={socialIconSx}
              >
                <LinkedInIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                component="a"
                href="https://github.com/ObsidianRidgeAI"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                sx={socialIconSx}
              >
                <GitHubIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                component="a"
                href="https://www.youtube.com/@ObsidianRidgeAI"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                sx={socialIconSx}
              >
                <YouTubeIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </Grid>

          {/* Links Columns */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Typography variant="overline" component="p" sx={columnLabelSx}>
              EXPLORE
            </Typography>
            <Stack spacing={1.5}>
              {exploreLinks.map((link) => (
                <Link
                  key={link.label}
                  component={RouterLink}
                  to={link.href}
                  underline="none"
                  sx={footerLinkSx}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Typography variant="overline" component="p" sx={columnLabelSx}>
              COMPANY
            </Typography>
            <Stack spacing={1.5}>
              {companyLinks.map((link) => (
                <Link
                  key={link.label}
                  component={RouterLink}
                  to={link.href}
                  underline="none"
                  sx={footerLinkSx}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Topics Column - top-tag internal linking (/?tag= contract) */}
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <Typography variant="overline" component="p" sx={columnLabelSx}>
              TOPICS
            </Typography>
            <Stack spacing={1.5}>
              {topicLinks.map((t) => (
                <Link
                  key={t.tag}
                  component={RouterLink}
                  to={`/?tag=${encodeURIComponent(t.tag)}`}
                  underline="none"
                  sx={footerLinkSx}
                >
                  {t.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Newsletter Column */}
          <Grid size={{ xs: 12, sm: 12, md: 3 }}>
            <Typography variant="overline" component="p" sx={columnLabelSx}>
              STAY IN THE LOOP
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
              Stay up to date with our latest articles, briefings, and guides on AI-assisted
              development.
            </Typography>
            <Box
              component="form"
              onSubmit={handleNewsletterSubmit}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <TextField
                placeholder="Enter your work email"
                aria-label="Email address for newsletter"
                fullWidth
                variant="outlined"
                size="small"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    pr: 0.5,
                    "& fieldset": { borderColor: "divider" },
                    "&:hover fieldset": { borderColor: "text.secondary" },
                    "&.Mui-focused fieldset": { borderColor: "secondary.main" },
                    "& input": {
                      py: 1,
                      fontSize: "0.875rem",
                    },
                  },
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          color="secondary"
                          aria-label="Subscribe to newsletter"
                          size="small"
                          type="submit"
                        >
                          <ArrowForwardIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              {/* Full sentence, so Schibsted (body2) — mono is metadata-only. */}
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                By subscribing, you agree to our Privacy Policy and consent to receive updates.
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Legal row */}
        <Box
          sx={{
            mt: 8,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "center", md: "flex-start" },
            gap: 3,
            textAlign: { xs: "center", md: "left" },
          }}
        >
          <Typography variant="caption" color="text.secondary">
            &copy; {new Date().getFullYear()} Obsidian Ridge AI &middot; Obsidian Ridge Holdings,
            LLC
          </Typography>
          {/* Single wrapping row with · separators (the separator is glued to
              the preceding link so a wrapped line never starts with "·"). */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: { xs: "center", md: "flex-start" },
              rowGap: 0.75,
            }}
          >
            {(
              [
                { label: "Privacy & Data", onClick: () => setPrivacyOpen(true) },
                { label: "Cookie Settings", onClick: () => setSettingsOpen(true) },
                { label: "Terms & Conditions", onClick: () => setTermsOpen(true) },
                { label: "Accessibility", onClick: () => setAccessibilityOpen(true) },
              ] as const
            ).map((item, index, items) => (
              <Box
                key={item.label}
                component="span"
                sx={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}
              >
                <Link
                  component="button"
                  variant="caption"
                  color="text.secondary"
                  underline="hover"
                  onClick={item.onClick}
                >
                  {item.label}
                </Link>
                {index < items.length - 1 && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    aria-hidden
                    sx={{ mx: 1 }}
                  >
                    &middot;
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Container>

      <PrivacyPolicyModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
      <AccessibilityModal open={accessibilityOpen} onClose={() => setAccessibilityOpen(false)} />
    </Box>
  );
};
